import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    useTheme,
    Chip,
} from '@mui/material';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';

import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

const CTASection: React.FC = () => {
  const { isEnabled } = useFeatureFlags();
  const regOpen = isEnabled('registration_open');
    const theme = useTheme();
    const navigate = useNavigate();

    return (
        <Box
            sx={{
                py: { xs: 8, md: 12 },
                background: theme.palette.background.default,
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={6} alignItems="center">
                    {/* Left Side - Text Content */}
                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="h2"
                            sx={{
                                fontSize: { xs: '2rem', md: '2.75rem' },
                                fontWeight: 'bold',
                                color: 'text.primary',
                                mb: 2,
                                lineHeight: 1.2,
                            }}
                        >
                            Start Planning Your Next Adventure
                        </Typography>
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '1rem', md: '1.15rem' },
                                lineHeight: 1.7,
                                mb: 4,
                            }}
                        >
                            Join travelers who are discovering a smarter way to plan trips. 
                            All the planning tools you need, plus a community to inspire you.
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => navigate(regOpen ? '/signup' : '/early-access')}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: '30px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                }}
                            >
                                Get Started Free
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={() => {
                                    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: '30px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                }}
                            >
                                See How It Works
                            </Button>
                        </Box>

                        {/* Trust Indicators */}
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                                label="✓ Free to join"
                                variant="outlined"
                                sx={{ borderColor: theme.palette.primary.main }}
                            />
                            <Chip
                                label="✓ No credit card required"
                                variant="outlined"
                                sx={{ borderColor: theme.palette.primary.main }}
                            />
                            <Chip
                                label="✓ Cancel anytime"
                                variant="outlined"
                                sx={{ borderColor: theme.palette.primary.main }}
                            />
                        </Box>
                    </Grid>

                    {/* Right Side - Explore the World Card */}
                    <Grid item xs={12} md={6}>
                        <Card
                            sx={{
                                borderRadius: '24px',
                                overflow: 'hidden',
                                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: '0 30px 80px rgba(0, 0, 0, 0.2)',
                                },
                            }}
                        >
                            {/* VIBRANT GRADIENT BACKGROUND with globe emoji */}
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '320px',
                                    background: `linear-gradient(145deg,
                                        ${theme.palette.primary.main} 0%,
                                        ${theme.palette.primary.dark} 40%,
                                        ${theme.palette.secondary.main} 100%)`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Decorative circles in background */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: '300px',
                                        height: '300px',
                                        borderRadius: '50%',
                                        background: 'rgba(255, 255, 255, 0.08)',
                                        top: '-100px',
                                        right: '-100px',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: '200px',
                                        height: '200px',
                                        borderRadius: '50%',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        bottom: '-50px',
                                        left: '-50px',
                                    }}
                                />

                                {/* COLORFUL WORLD GLOBE EMOJI - matches HeroSection */}
                                <Typography
                                    sx={{
                                        fontSize: '8rem',
                                        lineHeight: 1,
                                        mb: 2,
                                        filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
                                        animation: 'pulse 3s ease-in-out infinite',
                                        '@keyframes pulse': {
                                            '0%, 100%': { transform: 'scale(1)' },
                                            '50%': { transform: 'scale(1.05)' },
                                        },
                                    }}
                                >
                                    🌍
                                </Typography>

                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'white',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                        fontSize: { xs: '1.5rem', md: '1.8rem' },
                                    }}
                                >
                                    Explore the World
                                </Typography>
                            </Box>

                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Join Our Community
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
                                    Plan trips, share adventures, and discover itineraries from travelers worldwide.
                                </Typography>

                                {/* Feature Chips with outline icons */}
                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                    <Chip
                                        icon={<RocketLaunchOutlinedIcon sx={{ fontSize: '1.1rem' }} />}
                                        label="Explore"
                                        sx={{
                                            background: `${theme.palette.primary.main}15`,
                                            border: `1px solid ${theme.palette.primary.main}`,
                                            color: theme.palette.primary.main,
                                            fontWeight: 600,
                                            '& .MuiChip-icon': { color: theme.palette.primary.main },
                                        }}
                                    />
                                    <Chip
                                        icon={<GroupsOutlinedIcon sx={{ fontSize: '1.1rem' }} />}
                                        label="Connect"
                                        sx={{
                                            background: `${theme.palette.secondary.main}15`,
                                            border: `1px solid ${theme.palette.secondary.main}`,
                                            color: theme.palette.secondary.main,
                                            fontWeight: 600,
                                            '& .MuiChip-icon': { color: theme.palette.secondary.main },
                                        }}
                                    />
                                    <Chip
                                        icon={<MapOutlinedIcon sx={{ fontSize: '1.1rem' }} />}
                                        label="Plan"
                                        sx={{
                                            background: `${theme.palette.primary.main}15`,
                                            border: `1px solid ${theme.palette.primary.main}`,
                                            color: theme.palette.primary.main,
                                            fontWeight: 600,
                                            '& .MuiChip-icon': { color: theme.palette.primary.main },
                                        }}
                                    />
                                </Box>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={() => navigate('/community')}
                                    sx={{
                                        mt: 2,
                                        py: 1.5,
                                        borderRadius: '12px',
                                        fontWeight: 'bold',
                                        textTransform: 'none',
                                    }}
                                >
                                    Explore Community
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default CTASection;
